// Pre-built professional document templates for SoldiKeeper DMS
// Each template has category, description, classification, vault, tags, and structured content

export const DEFAULT_TEMPLATES = [
  {
    id: 'company-policy',
    name: 'Company Policy',
    category: 'Policy',
    description: 'Standard company policy document with compliance and approval sections',
    vault: 'procedures',
    classification: 'internal',
    tags: ['policy', 'compliance', 'hr'],
    icon: '📋',
    popularity: 0,
    content: `# Company Policy — {{policy_name}}

**Company:** {{company_name}}  
**Policy Number:** POL-{{year}}-001  
**Effective Date:** {{date}}  
**Author:** {{author}}  
**Department:** {{department}}  
**Version:** 1.0  

---

## 1. Purpose

This policy establishes clear guidelines for {{policy_name}} within {{company_name}}. It ensures consistent, compliant, and ethical practices across all departments and levels of the organization.

## 2. Scope

This policy applies to all employees, contractors, consultants, and third-party vendors who work for or on behalf of {{company_name}}. It covers all operations, locations, and business activities.

## 3. Policy Statement

{{company_name}} is committed to maintaining the highest standards of {{policy_name}}. All individuals subject to this policy are expected to:

- Understand and comply with the requirements set forth in this document
- Report any violations or concerns to their direct manager or HR
- Complete required training related to this policy
- Cooperate fully with any investigations related to policy violations

## 4. Definitions

| Term | Definition |
|------|-----------|
| Employee | Any full-time, part-time, or temporary staff member |
| Manager | Any person with direct reporting authority over others |
| Violation | Any action or omission contrary to this policy |

## 5. Procedures

### 5.1 Responsibilities

**Management:** Ensure all team members are aware of and adhere to this policy. Report violations promptly.

**Employees:** Read, understand, and follow this policy. Seek clarification when needed.

**HR Department:** Maintain this policy, provide training, and handle investigations.

### 5.2 Implementation

1. All relevant personnel receive a copy of this policy upon hire
2. Annual acknowledgment forms are collected by HR
3. Updates are communicated within 30 days of any changes
4. Training is provided as required

## 6. Compliance & Consequences

Violation of this policy may result in disciplinary action, up to and including termination of employment. Serious violations may be referred to legal authorities.

## 7. Review & Updates

This policy is reviewed annually by {{department}} and updated as needed to reflect changes in law, regulation, or company practice.

## 8. Approval

| Role | Name | Signature | Date |
|------|------|-----------|------|
| Author | {{author}} | _____________ | {{date}} |
| Department Head | _____________ | _____________ | _____ |
| HR Director | _____________ | _____________ | _____ |
| CEO | _____________ | _____________ | _____ |

---
*This document is the property of {{company_name}}. Unauthorized distribution is prohibited.*`
  },

  {
    id: 'sop',
    name: 'Standard Operating Procedure (SOP)',
    category: 'Operations',
    description: 'Detailed step-by-step procedure document with roles and references',
    vault: 'procedures',
    classification: 'internal',
    tags: ['sop', 'procedure', 'operations'],
    icon: '⚙️',
    popularity: 0,
    content: `# Standard Operating Procedure — {{procedure_name}}

**SOP Number:** SOP-{{department}}-{{year}}-001  
**Title:** {{procedure_name}}  
**Department:** {{department}}  
**Author:** {{author}}  
**Date:** {{date}}  
**Version:** 1.0  
**Status:** Active  

---

## 1. Objective

To provide clear, step-by-step instructions for {{procedure_name}} ensuring consistent quality, safety, and compliance across all operations at {{company_name}}.

## 2. Scope

This SOP applies to all personnel within {{department}} who are involved in {{procedure_name}}. It covers all phases from initiation to completion.

## 3. Responsibilities

| Role | Responsibility |
|------|---------------|
| Procedure Owner | {{author}} — Maintains and updates this SOP |
| Operator | Executes the procedure as documented |
| Supervisor | Verifies correct execution and signs off |
| Quality Control | Reviews output for compliance |

## 4. Prerequisites & Materials

Before beginning this procedure, ensure:
- [ ] All required materials and equipment are available
- [ ] Personnel have completed required training
- [ ] Safety equipment is available and inspected
- [ ] System access and permissions are confirmed
- [ ] Previous step/SOP has been completed (if applicable)

**Required materials:**
- Item 1
- Item 2
- Item 3

## 5. Procedure Steps

### Step 1: Preparation
1. Review this SOP completely before starting
2. Gather all required materials listed in Section 4
3. Verify system status and readiness
4. Document start time: ___________

### Step 2: Execution
1. [Detailed action step]
2. [Detailed action step]
3. [Detailed action step — include specifics, quantities, tolerances]
4. [Checkpoint: verify result before proceeding]

### Step 3: Quality Check
1. Review output against acceptance criteria
2. Document measurements/results
3. Escalate to supervisor if criteria not met

### Step 4: Completion
1. Clean and secure equipment/workspace
2. Document completion time: ___________
3. Submit completion record to supervisor
4. File documentation per records management procedure

## 6. Acceptance Criteria

The procedure is considered successfully completed when:
- All steps are executed in sequence
- Output meets quality standards
- Documentation is complete and signed
- No safety incidents occurred

## 7. Troubleshooting

| Issue | Possible Cause | Resolution |
|-------|---------------|------------|
| [Common issue] | [Root cause] | [Action to take] |
| [Common issue] | [Root cause] | [Action to take] |

## 8. Related Documents

- Policy: POL-{{year}}-001
- Training Record: TRN-{{department}}-001
- Quality Standard: QS-001

## 9. Revision History

| Version | Date | Author | Changes |
|---------|------|--------|---------|
| 1.0 | {{date}} | {{author}} | Initial release |

---
*Approved by: _____________ Date: _____________*`
  },

  {
    id: 'meeting-minutes',
    name: 'Meeting Minutes',
    category: 'Operations',
    description: 'Professional meeting record with decisions, action items and follow-ups',
    vault: 'procedures',
    classification: 'internal',
    tags: ['meeting', 'minutes', 'decisions', 'actions'],
    icon: '📝',
    popularity: 0,
    content: `# Meeting Minutes

**Meeting Title:** {{meeting_title}}  
**Date:** {{date}}  
**Time:** ____:____ to ____:____  
**Location / Platform:** _______________  
**Facilitator:** {{author}}  
**Minutes Recorded by:** {{author}}  

---

## Attendees

| Name | Role / Department | Present |
|------|------------------|---------|
| {{author}} | {{department}} | ✓ |
| _____________ | _____________ | ✓ |
| _____________ | _____________ | ✓ |
| _____________ | _____________ | ✗ (Apologies) |

**Quorum:** ☐ Yes ☐ No

---

## Agenda

1. Welcome and introductions
2. Review of previous meeting actions
3. [Agenda item 1]
4. [Agenda item 2]
5. [Agenda item 3]
6. Any other business
7. Next meeting planning

---

## Previous Actions Review

| Action | Owner | Due Date | Status |
|--------|-------|----------|--------|
| [Previous action] | [Name] | [Date] | ✓ Complete / ⏳ In Progress / ✗ Overdue |

---

## Discussion & Decisions

### Item 1: [Agenda Item]

**Discussion:** _____________________________________________

**Decision made:** ☐ Yes ☐ No ☐ Deferred

**Decision details:** _______________________________________

---

### Item 2: [Agenda Item]

**Discussion:** _____________________________________________

**Decision made:** ☐ Yes ☐ No ☐ Deferred

**Decision details:** _______________________________________

---

### Item 3: [Agenda Item]

**Discussion:** _____________________________________________

**Decision made:** ☐ Yes ☐ No ☐ Deferred

**Decision details:** _______________________________________

---

## Action Items

| # | Action | Owner | Due Date | Priority |
|---|--------|-------|----------|----------|
| 1 | [Action description] | [Name] | [Date] | High |
| 2 | [Action description] | [Name] | [Date] | Medium |
| 3 | [Action description] | [Name] | [Date] | Low |

---

## Next Meeting

**Date:** _____________  
**Time:** _____________  
**Location:** _____________  
**Proposed Agenda:** _____________  

---

## Approval

These minutes were reviewed and approved as an accurate record:

**Facilitator:** _____________ Date: _____________  
**[Next Reviewer]:** _____________ Date: _____________  

*Minutes distributed to attendees on: {{date}}*`
  },

  {
    id: 'project-proposal',
    name: 'Project Proposal',
    category: 'Operations',
    description: 'Executive project proposal with timeline, budget and risk assessment',
    vault: 'procedures',
    classification: 'confidential',
    tags: ['project', 'proposal', 'business', 'budget'],
    icon: '🚀',
    popularity: 0,
    content: `# Project Proposal

**Project Title:** {{project_name}}  
**Submitted by:** {{author}}  
**Department:** {{department}}  
**Date:** {{date}}  
**Version:** 1.0  
**Status:** Draft / Pending Approval  

---

## Executive Summary

This proposal outlines the business case for {{project_name}} at {{company_name}}. The project addresses [key business problem] by [proposed solution], with an expected return of [expected outcome] within [timeframe].

**Estimated Budget:** $___________  
**Timeline:** ___ months  
**Priority:** High / Medium / Low  
**Sponsor:** _____________  

---

## 1. Problem Statement

### 1.1 Current Situation

Describe the current state, challenges, and pain points that this project addresses. Include quantitative data where possible (costs, time lost, error rates, customer complaints, etc.).

### 1.2 Business Impact

- **Revenue impact:** $___________ annually
- **Cost impact:** $___________ in inefficiencies
- **Risk exposure:** [Regulatory / Operational / Reputational]
- **Stakeholders affected:** [List departments/teams/customers]

---

## 2. Proposed Solution

### 2.1 Solution Overview

Describe the proposed solution at a high level. What will be built, changed, or implemented?

### 2.2 Key Features / Deliverables

- Deliverable 1: [Description]
- Deliverable 2: [Description]
- Deliverable 3: [Description]

### 2.3 Success Metrics (KPIs)

| Metric | Current | Target | Measurement Method |
|--------|---------|--------|-------------------|
| [Metric 1] | [Value] | [Target] | [How measured] |
| [Metric 2] | [Value] | [Target] | [How measured] |

---

## 3. Scope

### 3.1 In Scope

- [Item in scope]
- [Item in scope]
- [Item in scope]

### 3.2 Out of Scope

- [Item explicitly excluded]
- [Item explicitly excluded]

---

## 4. Timeline

| Phase | Activities | Duration | Start | End |
|-------|-----------|----------|-------|-----|
| Phase 1: Discovery | Requirements gathering, analysis | 2 weeks | [Date] | [Date] |
| Phase 2: Design | Solution design, prototyping | 3 weeks | [Date] | [Date] |
| Phase 3: Implementation | Development, configuration | 6 weeks | [Date] | [Date] |
| Phase 4: Testing | UAT, QA, bug fixes | 2 weeks | [Date] | [Date] |
| Phase 5: Launch | Go-live, training, handover | 1 week | [Date] | [Date] |

---

## 5. Budget

| Category | Estimated Cost | Notes |
|----------|---------------|-------|
| Personnel (Internal) | $_________ | ___ FTE @ ___ hours |
| External Vendors | $_________ | [Vendor names] |
| Software / Licenses | $_________ | [Tools] |
| Hardware | $_________ | [Equipment] |
| Training | $_________ | [Programs] |
| Contingency (15%) | $_________ | |
| **TOTAL** | **$_________** | |

**Funding Source:** [Budget line / Department / Grant]

---

## 6. Risk Assessment

| Risk | Probability | Impact | Mitigation |
|------|------------|--------|-----------|
| [Risk 1] | High/Med/Low | High/Med/Low | [Mitigation strategy] |
| [Risk 2] | High/Med/Low | High/Med/Low | [Mitigation strategy] |
| [Risk 3] | High/Med/Low | High/Med/Low | [Mitigation strategy] |

---

## 7. Team

| Role | Name | Commitment | Responsibilities |
|------|------|-----------|-----------------|
| Project Sponsor | _____________ | Advisory | Executive support, budget approval |
| Project Manager | {{author}} | 50% | Day-to-day management, reporting |
| [Technical Lead] | _____________ | 100% | Solution delivery |
| [SME] | _____________ | 25% | Subject matter expertise |

---

## 8. Approval & Sign-Off

| Role | Decision | Signature | Date |
|------|----------|-----------|------|
| Department Head | ☐ Approve ☐ Reject ☐ Revise | _____________ | _____ |
| Finance | ☐ Approve ☐ Reject ☐ Revise | _____________ | _____ |
| CEO / Executive | ☐ Approve ☐ Reject ☐ Revise | _____________ | _____ |

**Comments:** _________________________________________________

---
*Confidential — {{company_name}} Internal Use Only*`
  },

  {
    id: 'nda',
    name: 'Non-Disclosure Agreement (NDA)',
    category: 'Legal',
    description: 'Mutual NDA template with standard confidentiality provisions',
    vault: 'legal',
    classification: 'confidential',
    tags: ['nda', 'legal', 'confidentiality', 'contract'],
    icon: '⚖️',
    popularity: 0,
    content: `# Non-Disclosure Agreement

**This Non-Disclosure Agreement** ("Agreement") is entered into as of {{date}} ("Effective Date") by and between:

**Disclosing Party:**  
{{company_name}}, a company incorporated under the laws of _______________, with its principal place of business at _______________ ("Company")

**AND**

**Receiving Party:**  
_______________, [an individual / a company incorporated under the laws of _______________], with address at _______________ ("Recipient")

Collectively referred to as the "Parties" and individually as a "Party."

---

## 1. Definition of Confidential Information

"Confidential Information" means any and all non-public information, whether written, oral, or in electronic form, disclosed by the Disclosing Party to the Receiving Party, including but not limited to:

- Business plans, strategies, and financial information
- Technical data, trade secrets, and know-how
- Product and service information, including features under development
- Customer and supplier lists and information
- Employee information and compensation data
- Software, algorithms, and source code
- Any information marked as "Confidential," "Proprietary," or similarly designated

**Exclusions:** Confidential Information does not include information that:
1. Was already known to the Receiving Party prior to disclosure
2. Is or becomes publicly known through no breach of this Agreement
3. Is independently developed by the Receiving Party without use of Confidential Information
4. Is disclosed to the Receiving Party by a third party without restriction

---

## 2. Obligations of Receiving Party

The Receiving Party agrees to:

2.1 **Hold in confidence** all Confidential Information received and protect it with at least the same degree of care used for its own confidential information, but no less than reasonable care.

2.2 **Use Confidential Information only** for the purpose of evaluating or pursuing a potential business relationship or transaction between the Parties (the "Purpose").

2.3 **Limit disclosure** of Confidential Information to employees, agents, or advisors who need to know for the Purpose and who are bound by confidentiality obligations at least as protective as this Agreement.

2.4 **Promptly notify** the Disclosing Party upon becoming aware of any unauthorized use or disclosure of Confidential Information.

2.5 **Not reverse engineer**, disassemble, or decompile any Confidential Information without prior written consent.

---

## 3. Term

This Agreement shall remain in effect from the Effective Date for a period of **[2] years** ("Term"), unless earlier terminated by mutual written agreement. Obligations with respect to Confidential Information disclosed during the Term shall survive for **[3] additional years** after termination.

---

## 4. Return or Destruction

Upon request by the Disclosing Party or upon termination of this Agreement, the Receiving Party shall promptly return or certifiably destroy all Confidential Information, including copies and derivatives, and provide written confirmation of such action within 10 business days.

---

## 5. No License

Nothing in this Agreement grants the Receiving Party any rights in or to the Confidential Information except as expressly set forth herein. No intellectual property rights are transferred or licensed.

---

## 6. Governing Law & Dispute Resolution

This Agreement shall be governed by and construed in accordance with the laws of _______________. Any disputes shall be resolved by binding arbitration in _______________, except that either Party may seek injunctive relief in any court of competent jurisdiction.

---

## 7. General Provisions

- **Entire Agreement:** This Agreement constitutes the entire agreement between the Parties regarding confidentiality.
- **Amendment:** Any amendment must be in writing and signed by both Parties.
- **Severability:** If any provision is unenforceable, the remaining provisions remain in full force.
- **No Waiver:** Failure to enforce any provision shall not constitute a waiver of future enforcement rights.

---

## 8. Signatures

**For {{company_name}}:**

Name: _________________________  
Title: _________________________  
Signature: _________________________  
Date: _________________________  

**For Recipient:**

Name: _________________________  
Title: _________________________  
Signature: _________________________  
Date: _________________________  

---
*This agreement is legally binding. Consult legal counsel before signing.*`
  },

  {
    id: 'incident-report',
    name: 'Incident Report',
    category: 'Operations',
    description: 'Comprehensive incident documentation with root cause and corrective actions',
    vault: 'compliance',
    classification: 'confidential',
    tags: ['incident', 'safety', 'compliance', 'report'],
    icon: '🚨',
    popularity: 0,
    content: `# Incident Report

**Report Number:** INC-{{year}}-_____  
**Company:** {{company_name}}  
**Department:** {{department}}  
**Report Prepared by:** {{author}}  
**Date of Report:** {{date}}  

---

## Section 1: Incident Details

**Date of Incident:** _____________  
**Time of Incident:** _____________  
**Location:** _____________  
**Incident Type:** ☐ Safety ☐ Security ☐ IT/System ☐ Compliance ☐ Customer ☐ Environmental ☐ Other: _____

**Severity Level:**
- ☐ **Critical** — Operations halted, significant harm or loss
- ☐ **High** — Major disruption, significant risk
- ☐ **Medium** — Partial disruption, moderate risk
- ☐ **Low** — Minor issue, minimal impact

**Status:** ☐ Open ☐ Under Investigation ☐ Resolved ☐ Closed

---

## Section 2: Incident Description

### 2.1 What Happened?

Provide a detailed, factual account of what occurred. Include timeline of events, sequence of actions, and observable facts. Avoid assumptions or opinions.

_[Detailed narrative description]_

### 2.2 Immediate Impact

**Personnel affected:** _____________  
**Systems/equipment affected:** _____________  
**Data affected:** ☐ Yes ☐ No — If yes: _____________  
**Financial impact (estimated):** $_____________  
**Customer impact:** ☐ Yes ☐ No — If yes: _____________  

### 2.3 Timeline of Events

| Time | Event |
|------|-------|
| ____:____ | [Event description] |
| ____:____ | Incident detected / reported |
| ____:____ | Initial response initiated |
| ____:____ | Containment achieved |
| ____:____ | Resolution / closure |

---

## Section 3: People Involved

### 3.1 Witnesses

| Name | Role | Contact | Statement Summary |
|------|------|---------|------------------|
| _____________ | _____________ | _____________ | _____________ |

### 3.2 Responders

| Name | Role | Actions Taken |
|------|------|--------------|
| {{author}} | Report Author | Documented incident |
| _____________ | First Responder | _____________ |

---

## Section 4: Root Cause Analysis

### 4.1 Immediate Cause

The direct cause of the incident: _____________________________________________

### 4.2 Contributing Factors

- ☐ Human error — Description: _____________
- ☐ Equipment failure — Description: _____________
- ☐ Process/procedure gap — Description: _____________
- ☐ Environmental factors — Description: _____________
- ☐ Communication breakdown — Description: _____________
- ☐ Training deficiency — Description: _____________
- ☐ Other: _____________

### 4.3 Root Cause

After analysis, the underlying root cause is: ____________________________________

**Root Cause Category:** ☐ People ☐ Process ☐ Technology ☐ Environment ☐ Management

---

## Section 5: Corrective Actions

| # | Action | Responsible Person | Due Date | Status |
|---|--------|-------------------|----------|--------|
| 1 | [Immediate containment action] | {{author}} | {{date}} | ☐ Complete |
| 2 | [Short-term corrective action] | _____________ | _______ | ☐ Pending |
| 3 | [Long-term preventive action] | _____________ | _______ | ☐ Planned |
| 4 | [Process/training update] | _____________ | _______ | ☐ Planned |

---

## Section 6: Regulatory & Compliance Requirements

**Regulatory notification required:** ☐ Yes ☐ No  
**If yes, authority to notify:** _____________  
**Notification deadline:** _____________  
**Notification sent:** ☐ Yes ☐ No — Date: _____________  

**Insurance claim required:** ☐ Yes ☐ No  
**Legal review required:** ☐ Yes ☐ No  

---

## Section 7: Approvals

| Role | Name | Signature | Date |
|------|------|-----------|------|
| Report Author | {{author}} | _____________ | {{date}} |
| Department Manager | _____________ | _____________ | _____ |
| Safety/Compliance Officer | _____________ | _____________ | _____ |
| HR (if personnel involved) | _____________ | _____________ | _____ |

---
*This report is confidential. Distribution is restricted to authorized personnel only.*`
  },

  {
    id: 'financial-report',
    name: 'Financial Report',
    category: 'Financial',
    description: 'Period financial performance report with P&L, commentary and forecast',
    vault: 'financial',
    classification: 'restricted',
    tags: ['financial', 'report', 'revenue', 'p&l', 'forecast'],
    icon: '💰',
    popularity: 0,
    content: `# Financial Performance Report

**Company:** {{company_name}}  
**Period:** _____________ [Month/Quarter/Year]  
**Prepared by:** {{author}}  
**Department:** Finance / {{department}}  
**Date:** {{date}}  
**Classification:** RESTRICTED — Finance and Executive Team Only  

---

## Executive Summary

This report presents the financial performance of {{company_name}} for the period ending _____________. 

**Key Highlights:**
- Revenue: $_________ vs. Budget: $_________  (___% variance)
- Net Profit: $_________ vs. Budget: $_________ (___% variance)
- Operating Margin: ___%
- [Key achievement or concern]

---

## 1. Income Statement

| Category | Current Period | Prior Period | YTD | Budget | Variance |
|----------|--------------|-------------|-----|--------|----------|
| **Revenue** | | | | | |
| Product Sales | $_________ | $_________ | $_________ | $_________ | ___% |
| Service Revenue | $_________ | $_________ | $_________ | $_________ | ___% |
| Other Revenue | $_________ | $_________ | $_________ | $_________ | ___% |
| **Total Revenue** | **$_________** | **$_________** | **$_________** | **$_________** | **___%** |
| | | | | | |
| **Cost of Revenue** | | | | | |
| Direct Costs | $_________ | $_________ | $_________ | $_________ | ___% |
| **Gross Profit** | **$_________** | **$_________** | **$_________** | **$_________** | **___%** |
| **Gross Margin** | **___%** | **___%** | **___%** | **___%** | |
| | | | | | |
| **Operating Expenses** | | | | | |
| Personnel | $_________ | $_________ | $_________ | $_________ | ___% |
| Marketing | $_________ | $_________ | $_________ | $_________ | ___% |
| Technology | $_________ | $_________ | $_________ | $_________ | ___% |
| Office & Admin | $_________ | $_________ | $_________ | $_________ | ___% |
| Other OpEx | $_________ | $_________ | $_________ | $_________ | ___% |
| **Total OpEx** | **$_________** | **$_________** | **$_________** | **$_________** | **___%** |
| | | | | | |
| **EBITDA** | **$_________** | **$_________** | **$_________** | **$_________** | **___%** |
| Depreciation | $_________ | $_________ | $_________ | $_________ | |
| **Operating Profit** | **$_________** | **$_________** | **$_________** | **$_________** | **___%** |
| Interest & Taxes | $_________ | $_________ | $_________ | $_________ | |
| **Net Profit** | **$_________** | **$_________** | **$_________** | **$_________** | **___%** |
| **Net Margin** | **___%** | **___%** | **___%** | **___%** | |

---

## 2. Cash Flow Summary

| Category | Current Period | YTD |
|----------|--------------|-----|
| Operating Cash Flow | $_________ | $_________ |
| Investing Cash Flow | $_________ | $_________ |
| Financing Cash Flow | $_________ | $_________ |
| **Net Cash Movement** | **$_________** | **$_________** |
| **Closing Cash Balance** | **$_________** | |

---

## 3. Management Commentary

### 3.1 Revenue Performance

_[Explain revenue drivers, key contracts won/lost, market conditions, pricing changes]_

### 3.2 Cost Analysis

_[Explain significant cost movements, efficiency gains/losses, one-time items]_

### 3.3 Key Risks

- **Risk 1:** [Description] — Potential impact: $_________ — Mitigation: _____________
- **Risk 2:** [Description] — Potential impact: $_________ — Mitigation: _____________

---

## 4. Forecast

| Period | Revenue | Net Profit | Net Margin |
|--------|---------|-----------|-----------|
| Next Month | $_________ | $_________ | ___% |
| Next Quarter | $_________ | $_________ | ___% |
| Full Year | $_________ | $_________ | ___% |

**Assumptions:** _________________________________________________

---

## 5. Approvals

| Role | Name | Signature | Date |
|------|------|-----------|------|
| Prepared by | {{author}} | _____________ | {{date}} |
| Finance Director | _____________ | _____________ | _____ |
| CEO | _____________ | _____________ | _____ |

---
*RESTRICTED — This document contains confidential financial information. Unauthorized distribution is strictly prohibited.*`
  },

  {
    id: 'employee-handbook',
    name: 'Employee Handbook Section',
    category: 'HR',
    description: 'Professional HR handbook section template with policy and acknowledgement',
    vault: 'hr',
    classification: 'internal',
    tags: ['hr', 'handbook', 'policy', 'employees'],
    icon: '👥',
    popularity: 0,
    content: `# Employee Handbook

## {{policy_name}}

**{{company_name}}**  
**Section:** _____ | **Policy Code:** HR-{{year}}-_____  
**Effective Date:** {{date}}  
**Last Reviewed:** {{date}}  
**Approved by:** HR Director  

---

## About This Handbook

This handbook section provides information about {{company_name}}'s policies, practices, and expectations. It is intended to help employees understand their rights and responsibilities. This handbook does not constitute an employment contract.

{{company_name}} reserves the right to modify, change, or eliminate any policy at any time with appropriate notice to employees.

---

## 1. Policy Statement

{{company_name}} is committed to creating a fair, inclusive, and productive work environment. This section covers {{policy_name}} and outlines the expectations for all employees.

**Our core values:**
- **Integrity:** We do what we say and say what we do
- **Respect:** We treat every person with dignity and fairness
- **Excellence:** We strive for the highest quality in everything we do
- **Collaboration:** We achieve more together than apart

---

## 2. Applicability

This policy applies to:
- All full-time and part-time employees
- Temporary and contract workers
- Interns and apprentices
- All locations and remote workers

---

## 3. Policy Details

### 3.1 [Key Policy Area]

_[Detailed policy provisions — explain the rules, expectations, and entitlements clearly in plain language]_

**Employees are entitled to:**
- [Entitlement 1]
- [Entitlement 2]
- [Entitlement 3]

**Employees are expected to:**
- [Expectation 1]
- [Expectation 2]
- [Expectation 3]

### 3.2 Eligibility & Conditions

_[Who qualifies, when, and under what conditions. Include any waiting periods, performance requirements, or restrictions]_

### 3.3 Process / How to Apply

1. [Step 1]
2. [Step 2]
3. [Step 3]
4. Decisions will be communicated within [timeframe]

### 3.4 Management Discretion

In all cases, management retains reasonable discretion to apply these policies fairly while considering operational needs. Employees who believe a policy has been applied unfairly may use the grievance procedure outlined in Section ___.

---

## 4. Non-Compliance

Failure to comply with this policy may result in:
- Verbal or written warning
- Performance improvement plan
- Suspension
- Termination of employment

The severity of consequences will be proportionate to the nature and frequency of the violation.

---

## 5. Privacy & Confidentiality

Information related to this policy (including personal details, medical information, or compensation) will be treated as strictly confidential and shared only on a need-to-know basis in accordance with {{company_name}}'s Data Privacy Policy.

---

## 6. Questions & Grievances

For questions about this policy, contact:  
**HR Department:** hr@{{company_name}}.com  
**Phone:** _____________  
**Office Hours:** Monday–Friday, ____:____ – ____:____

For formal grievances, refer to the Grievance Procedure in Section ___ of this handbook.

---

## 7. Employee Acknowledgement

I, the undersigned, acknowledge that I have received, read, and understood the {{policy_name}} policy as described in this handbook section. I agree to comply with all provisions and understand that violations may result in disciplinary action.

**Employee Name (Print):** _________________________  
**Employee Signature:** _________________________  
**Date:** _________________________  
**Department:** _________________________  
**Manager:** _________________________  

*Please return a signed copy to HR within 5 business days of receipt.*`
  },

  {
    id: 'service-agreement',
    name: 'Service Agreement',
    category: 'Legal',
    description: 'Professional services agreement with deliverables, payment and IP terms',
    vault: 'contracts',
    classification: 'confidential',
    tags: ['contract', 'services', 'agreement', 'legal'],
    icon: '📄',
    popularity: 0,
    content: `# Service Agreement

**This Service Agreement** ("Agreement") is made and entered into as of {{date}} ("Effective Date") between:

**Service Provider:**  
{{company_name}} ("Provider")  
Address: _________________________  
Registration No.: _________________________  

**AND**

**Client:**  
_________________________ ("Client")  
Address: _________________________  
Registration No.: _________________________  

---

## 1. Services

### 1.1 Description of Services

Provider agrees to provide the following professional services ("Services") to Client:

_[Detailed description of services, deliverables, and scope of work]_

### 1.2 Deliverables

| Deliverable | Description | Due Date | Acceptance Criteria |
|-------------|-------------|----------|-------------------|
| [D1] | _____________ | _________ | _____________ |
| [D2] | _____________ | _________ | _____________ |
| [D3] | _____________ | _________ | _____________ |

### 1.3 Out of Scope

The following are expressly excluded from this Agreement:
- [Exclusion 1]
- [Exclusion 2]

Any out-of-scope work requires a written Change Order signed by both Parties.

---

## 2. Term

This Agreement commences on {{date}} and continues until _______________, unless earlier terminated pursuant to Section 7 hereof.

---

## 3. Compensation & Payment

### 3.1 Fees

| Description | Amount | Payment Terms |
|-------------|--------|--------------|
| [Service/Phase 1] | $_________ | Upon signature |
| [Service/Phase 2] | $_________ | Upon delivery |
| [Service/Phase 3] | $_________ | Net 30 from invoice |
| **Total** | **$_________** | |

### 3.2 Expenses

Pre-approved travel and out-of-pocket expenses will be reimbursed at cost with supporting receipts, not to exceed $___________ without prior written approval.

### 3.3 Invoicing & Late Payment

Invoices are payable within **30 days** of receipt. Late payments incur interest at **1.5% per month** on the outstanding balance.

---

## 4. Client Obligations

Client agrees to:
- Provide timely access to required information, systems, and personnel
- Designate a primary point of contact for this engagement
- Review and respond to deliverables within [10] business days
- Obtain all necessary approvals and authorizations

---

## 5. Intellectual Property

### 5.1 Pre-existing IP

Each party retains ownership of its pre-existing intellectual property. Provider retains ownership of all methodologies, tools, and know-how developed independently.

### 5.2 Work Product

Upon full payment, Provider assigns to Client all rights, title, and interest in the deliverables specifically created for Client under this Agreement ("Work Product"), excluding any pre-existing IP incorporated therein.

### 5.3 License

Provider grants Client a perpetual, non-exclusive license to use any Provider IP incorporated in the Work Product solely for Client's internal business purposes.

---

## 6. Confidentiality

Both Parties agree to hold all Confidential Information in strict confidence and not disclose it to third parties without prior written consent. This obligation survives termination for **3 years**.

---

## 7. Termination

### 7.1 For Convenience

Either Party may terminate this Agreement with **30 days** written notice. Client shall pay for all Services rendered through the termination date.

### 7.2 For Cause

Either Party may terminate immediately upon written notice if the other Party: (a) materially breaches this Agreement and fails to cure within 15 days; (b) becomes insolvent or files for bankruptcy; (c) engages in fraudulent or illegal conduct.

---

## 8. Limitation of Liability

Provider's total liability shall not exceed the total fees paid in the **3 months** preceding the claim. Neither Party shall be liable for indirect, incidental, or consequential damages.

---

## 9. General Provisions

- **Governing Law:** The laws of _______________
- **Dispute Resolution:** Binding arbitration in _______________
- **Entire Agreement:** This Agreement supersedes all prior discussions
- **Amendment:** Changes require written agreement of both Parties
- **Force Majeure:** Neither Party liable for delays due to events beyond reasonable control

---

## 10. Signatures

**{{company_name}} (Provider):**

Name: _________________________  
Title: _________________________  
Signature: _________________________  
Date: _________________________  

**Client:**

Name: _________________________  
Title: _________________________  
Signature: _________________________  
Date: _________________________  

---
*Both parties should retain a signed copy of this agreement.*`
  },

  {
    id: 'risk-assessment',
    name: 'Risk Assessment',
    category: 'Compliance',
    description: 'Enterprise risk assessment with risk register, scoring matrix and treatment plans',
    vault: 'compliance',
    classification: 'confidential',
    tags: ['risk', 'assessment', 'compliance', 'governance'],
    icon: '🛡️',
    popularity: 0,
    content: `# Risk Assessment

**Organization:** {{company_name}}  
**Assessment Title:** {{project_name}} Risk Assessment  
**Department:** {{department}}  
**Prepared by:** {{author}}  
**Date:** {{date}}  
**Version:** 1.0  
**Review Date:** _____________ (Annual review recommended)  

---

## 1. Executive Summary

This risk assessment identifies, analyzes, and evaluates risks associated with {{project_name}} at {{company_name}}. The assessment covers [scope area] and provides treatment recommendations for each identified risk.

**Assessment Period:** _____________  
**Total Risks Identified:** _____  
**Critical Risks (score ≥ 15):** _____  
**High Risks (score 9–14):** _____  
**Medium Risks (score 4–8):** _____  
**Low Risks (score 1–3):** _____  

---

## 2. Risk Methodology

### 2.1 Risk Scoring Matrix

**Likelihood Scale:**
| Score | Level | Definition |
|-------|-------|-----------|
| 5 | Almost Certain | > 90% probability, likely to occur multiple times |
| 4 | Likely | 60-90% probability, likely to occur |
| 3 | Possible | 30-60% probability, could occur |
| 2 | Unlikely | 10-30% probability, unlikely to occur |
| 1 | Rare | < 10% probability, may occur in exceptional circumstances |

**Impact Scale:**
| Score | Level | Definition |
|-------|-------|-----------|
| 5 | Catastrophic | Severe loss, regulatory sanction, major reputational damage |
| 4 | Major | Significant financial loss, project failure, high reputational damage |
| 3 | Moderate | Moderate impact, manageable disruption, some reputational damage |
| 2 | Minor | Minor disruption, small financial loss, limited reputational impact |
| 1 | Negligible | Minimal impact, easily resolved internally |

**Risk Score = Likelihood × Impact**

| | **1 Negligible** | **2 Minor** | **3 Moderate** | **4 Major** | **5 Catastrophic** |
|---|---|---|---|---|---|
| **5 Almost Certain** | 5 | 10 | 15 🔴 | 20 🔴 | 25 🔴 |
| **4 Likely** | 4 | 8 | 12 🟠 | 16 🔴 | 20 🔴 |
| **3 Possible** | 3 | 6 | 9 🟠 | 12 🟠 | 15 🔴 |
| **2 Unlikely** | 2 | 4 | 6 🟡 | 8 🟡 | 10 🟠 |
| **1 Rare** | 1 | 2 | 3 🟡 | 4 🟡 | 5 🟡 |

🔴 Critical (15-25) | 🟠 High (9-14) | 🟡 Medium (4-8) | 🟢 Low (1-3)

---

## 3. Risk Register

| ID | Risk Description | Category | L | I | Score | Rating | Owner | Treatment | Residual Score |
|----|----------------|----------|---|---|-------|--------|-------|-----------|---------------|
| R01 | [Risk description] | Operational | _ | _ | _ | 🟡 | _______ | Mitigate | _ |
| R02 | [Risk description] | Financial | _ | _ | _ | 🟠 | _______ | Transfer | _ |
| R03 | [Risk description] | Compliance | _ | _ | _ | 🔴 | _______ | Mitigate | _ |
| R04 | [Risk description] | Reputational | _ | _ | _ | 🟡 | _______ | Accept | _ |
| R05 | [Risk description] | Strategic | _ | _ | _ | 🟠 | _______ | Mitigate | _ |

*L = Likelihood, I = Impact*

---

## 4. Risk Treatment Plans

### R01: [Risk Name]

**Description:** _____________________________________________  
**Current Controls:** _____________________________________________  
**Treatment Strategy:** ☐ Avoid ☐ Mitigate ☐ Transfer ☐ Accept  

**Treatment Actions:**
| Action | Owner | Due Date | Cost | Status |
|--------|-------|----------|------|--------|
| [Action 1] | {{author}} | _________ | $_____ | Pending |
| [Action 2] | _________ | _________ | $_____ | Pending |

**KRI (Key Risk Indicator):** _____________  
**Escalation Trigger:** _____________  

---

### R02: [Risk Name]

**Description:** _____________________________________________  
**Current Controls:** _____________________________________________  
**Treatment Strategy:** ☐ Avoid ☐ Mitigate ☐ Transfer ☐ Accept  

**Treatment Actions:**
| Action | Owner | Due Date | Cost | Status |
|--------|-------|----------|------|--------|
| [Action 1] | _________ | _________ | $_____ | Pending |

---

## 5. Monitoring & Review

**Risk Owner Meetings:** Monthly review of critical and high risks  
**Full Assessment Review:** Annually or following significant organizational changes  
**Escalation Protocol:** Critical risks (≥15) escalated to Board/Executive within 48 hours  

---

## 6. Approval

| Role | Name | Signature | Date |
|------|------|-----------|------|
| Risk Author | {{author}} | _____________ | {{date}} |
| Department Head | _____________ | _____________ | _____ |
| Risk/Compliance Officer | _____________ | _____________ | _____ |
| CEO / Board | _____________ | _____________ | _____ |

---
*CONFIDENTIAL — Risk assessments contain sensitive business information. Distribution restricted to authorized personnel.*`
  }
];

export const TEMPLATE_CATEGORIES = ['All', 'Policy', 'Operations', 'Legal', 'Financial', 'HR', 'Compliance'];

export const getTemplatesByCategory = (category) => {
  if (category === 'All') return DEFAULT_TEMPLATES;
  return DEFAULT_TEMPLATES.filter(t => t.category === category);
};

export const getTemplateById = (id) => DEFAULT_TEMPLATES.find(t => t.id === id);
